import { useState } from 'react'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { useRescheduleReservation } from '@/hooks/useRescheduleReservation'
import { ApiError, toApiError } from '@/lib/api/errors'
import { TIME_OF_DAY_OPTIONS } from '@/lib/venue/operatingHourTypes'
import {
  hasRescheduleFormErrors,
  initialRescheduleFormState,
  toRescheduleInput,
  validateRescheduleForm,
  type RescheduleFormErrors,
} from '@/lib/venue/reservationRescheduleViewModel'
import type { ReservationDetailView } from '@/lib/venue/reservationTypes'

function FieldError({ message }: { message: string | undefined }) {
  if (!message) return null
  return <p className="mt-1 text-xs text-danger-500">{message}</p>
}

/**
 * 예약 변경 모달(설계 §2, #669 §1) — 코트·날짜·시간 재선택 + 사유.
 *
 * 🚨 성공 응답이 곧 최신 상세다 — `useRescheduleReservation` 이 캐시를 직접 갱신하므로 이 모달은
 * 상세를 다시 조회하지 않는다. 🚨 슬롯 정렬·단위 배수 등 서버 전용 규칙은 클라이언트에서 재검증하지
 * 않고 서버 400 메시지를 그대로 보여준다(중복 규칙 보유 금지).
 */
export function ReservationRescheduleModal({
  venueId,
  detail,
  courts,
  onClose,
}: {
  venueId: string
  detail: ReservationDetailView
  courts: { courtId: number; name: string }[]
  onClose: () => void
}) {
  const current = {
    courtId: detail.reservation.courtId,
    useDate: detail.pricing.useDate,
    startTime: detail.pricing.startTime,
    endTime: detail.pricing.endTime,
  }

  const [form, setForm] = useState(() => initialRescheduleFormState(current))
  const [errors, setErrors] = useState<RescheduleFormErrors>({})
  const [generalError, setGeneralError] = useState<ApiError | null>(null)

  const rescheduleMutation = useRescheduleReservation(venueId, detail.reservation.id)

  function updateField<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function handleSubmit() {
    const validationErrors = validateRescheduleForm(form, current)
    if (hasRescheduleFormErrors(validationErrors)) {
      setErrors(validationErrors)
      return
    }
    setErrors({})
    setGeneralError(null)

    try {
      await rescheduleMutation.mutateAsync(toRescheduleInput(form))
      onClose()
    } catch (error) {
      setGeneralError(toApiError(error))
    }
  }

  return (
    <Modal title="예약 변경" onClose={onClose}>
      <div className="space-y-4">
        <p className="rounded-lg bg-slateish-50 px-3 py-2 text-xs text-slateish-600">
          코트·날짜·시간을 변경합니다. 변경 후 예약자에게 알림이 발송됩니다. 결제 금액은 변경되지 않습니다.
        </p>

        {generalError ? (
          <Alert tone="danger">
            <p>{generalError.message}</p>
            {generalError.isRescheduleConflict && generalError.rescheduleConflict?.retriable ? (
              <p className="mt-1 text-xs">잠시 후 다시 시도해 주세요.</p>
            ) : null}
          </Alert>
        ) : null}

        <FieldError message={errors.general} />

        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="reschedule-court">
            코트
          </label>
          <Select
            id="reschedule-court"
            value={form.courtId}
            aria-invalid={Boolean(errors.courtId)}
            onChange={(event) => {
              updateField('courtId', Number(event.target.value))
            }}
          >
            {courts.map((court) => (
              <option key={court.courtId} value={court.courtId}>
                {court.name}
              </option>
            ))}
          </Select>
          <FieldError message={errors.courtId} />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="reschedule-date">
            날짜
          </label>
          <Input
            id="reschedule-date"
            type="date"
            value={form.useDate}
            aria-invalid={Boolean(errors.useDate)}
            onChange={(event) => {
              updateField('useDate', event.target.value)
            }}
          />
          <FieldError message={errors.useDate} />
        </div>

        <div>
          <span className="mb-1.5 block text-sm font-medium text-ink-700">시간대</span>
          <div className="flex items-center gap-2">
            <Select
              aria-label="시작 시각"
              value={form.startTime}
              onChange={(event) => {
                updateField('startTime', event.target.value)
              }}
            >
              {TIME_OF_DAY_OPTIONS.map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </Select>
            <span className="text-slateish-400">~</span>
            <Select
              aria-label="종료 시각"
              value={form.endTime}
              onChange={(event) => {
                updateField('endTime', event.target.value)
              }}
            >
              {TIME_OF_DAY_OPTIONS.map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </Select>
          </div>
          <FieldError message={errors.time} />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="reschedule-reason">
            변경 사유
          </label>
          <Textarea
            id="reschedule-reason"
            rows={2}
            value={form.reason}
            placeholder="예) 코트 보수로 이동"
            aria-invalid={Boolean(errors.reason)}
            onChange={(event) => {
              updateField('reason', event.target.value)
            }}
          />
          <FieldError message={errors.reason} />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button
            type="button"
            disabled={rescheduleMutation.isPending}
            onClick={() => {
              void handleSubmit()
            }}
          >
            {rescheduleMutation.isPending ? '저장 중…' : '저장하기'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
